import React, { useMemo, lazy, Suspense, useCallback, useRef, useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { debounce } from 'debounce';
import { logExploreSearchBoxClicked, logSearchResultClicked } from '@alltrails/analytics/amplitudeFunctions';
import { SearchOrigin, SearchSuggestionSelectionType, WebSearchTab } from '@alltrails/analytics/amplitudeEnums';
import useSearch from 'hooks/useSearch';
import { DESKTOP_BREAKPOINT } from 'utils/constants/breakpoints';
import { ARROW_UP, ARROW_DOWN, ESC } from 'utils/constants/key_codes';
import SearchBar from 'components/search/SearchBar';
import { SearchInsightsContext } from 'components/SearchInsightsProvider';
import { algoliaTypeToAmplitudeType } from 'utils/amplitudeHelper';
import * as styles from './styles/styles.module.scss';
import { Context } from '@alltrails/shared/types/context';

const MobileSearchBar = lazy(() => import('../MobileSearchBar'));

type Props = {
  latLng?: (string | number)[];
  windowWidth?: number;
  context: Context;
};

const HomeSearchBar = ({ latLng = null, windowWidth = null, context }: Props) => {
  const searchContainer = useRef<HTMLDivElement>();

  const { currentUser, primaryAlgoliaIndexId } = context;

  const analyticsTagsParams = {
    user: currentUser,
    locale: context.languageRegionCode,
    origin: SearchOrigin.Homepage,
    mobile: context.mobileBrowser
  };

  const { updateSearchId, logClickEvent } = useContext(SearchInsightsContext);

  const { searchState, updateQuery, updateActiveTabFilter, toggleEngaged, updateActiveIndexResult } = useSearch({
    primaryAlgoliaIndexId,
    latLng,
    languageRegionCode: context.languageRegionCode,
    analyticsTagsParams
  });

  // Whenever we update the search id inform our global tracking context about it.
  useEffect(() => {
    if (searchState.queryID) {
      updateSearchId(searchState.queryID);
    }
  }, [updateSearchId, searchState.queryID]);

  const navigateToSearchResult = (result: any) => {
    window.location = result.url;
  };

  const handleSearchResultClick = useCallback(
    (result, idx) => {
      const searchTab = searchState.filters[searchState.activeFilterIndex].label;
      const contentId = result?.objectID ? result.objectID.split('-')[1] : '';
      logSearchResultClicked({
        click_position: idx,
        content_id: contentId,
        item_type: algoliaTypeToAmplitudeType(result?.type),
        location: SearchOrigin.Homepage,
        poi_type: null /* TODO: DISCO-604 add poi analytics */,
        query_length: searchState.query ? searchState.query.length : 0,
        selection_type: SearchSuggestionSelectionType.SearchSuggestion,
        search_tab: searchTab as WebSearchTab
      });
      logClickEvent(result?.objectID, idx + 1);
      navigateToSearchResult(result);
    },
    [searchState.query, searchState.filters, searchState.activeFilterIndex, logClickEvent]
  );

  const handleSearchBoxFocus = useCallback(() => {
    logExploreSearchBoxClicked({ search_origin: SearchOrigin.Homepage });
  }, []);

  const isMobileWidth = useMemo(() => windowWidth && windowWidth < DESKTOP_BREAKPOINT, [windowWidth]);

  const mobileSearchBar = useMemo(() => isMobileWidth && searchState.engaged, [isMobileWidth, searchState.engaged]);

  const checkForFocusOut = debounce(() => {
    if (searchContainer.current && !searchContainer.current.contains(document.activeElement)) {
      toggleEngaged(false);
    }
  }, 800);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // this handles keyboard interactions, for arrow_up and down as well as esc
    if (window.keyboardUser) {
      checkForFocusOut();
    }

    if ([ARROW_UP, ARROW_DOWN].includes(e.keyCode)) {
      e.preventDefault();
    }

    switch (e.keyCode) {
      case ESC:
        toggleEngaged(false);
        break;
      case ARROW_UP:
        if (searchState.activeIndexResult === 0) {
          updateActiveIndexResult(searchState.results.length - 1);
        } else {
          updateActiveIndexResult(searchState.activeIndexResult - 1);
        }
        break;
      case ARROW_DOWN:
        if (searchState.activeIndexResult + 1 === searchState.results.length) {
          updateActiveIndexResult(0);
        } else {
          updateActiveIndexResult(searchState.activeIndexResult + 1);
        }
        break;
      default:
        // Ignore.
        break;
    }
  };

  return (
    <>
      {mobileSearchBar &&
        ReactDOM.createPortal(
          <Suspense fallback={null}>
            <MobileSearchBar
              query={searchState.query}
              loading={searchState.loading}
              updateQuery={updateQuery}
              results={searchState.results}
              filters={searchState.filters}
              activeFilterIndex={searchState.activeFilterIndex}
              updateActiveTabFilter={updateActiveTabFilter}
              toggleEngaged={toggleEngaged}
              context={context}
              activeIndexResult={searchState.activeIndexResult}
              updateActiveIndexResult={updateActiveIndexResult}
              handleSearchResultClick={handleSearchResultClick}
              handleSearchBoxFocus={handleSearchBoxFocus}
            />
          </Suspense>,
          document.getElementById('modalPortal')
        )}
      <div role="presentation" ref={searchContainer} className={styles.container} onKeyUp={handleKeyUp}>
        <SearchBar
          query={searchState.query}
          loading={searchState.loading}
          updateQuery={updateQuery}
          results={searchState.results}
          filters={searchState.filters}
          activeFilterIndex={searchState.activeFilterIndex}
          updateActiveTabFilter={updateActiveTabFilter}
          toggleEngaged={toggleEngaged}
          engaged={searchState.engaged}
          context={context}
          activeIndexResult={searchState.activeIndexResult}
          updateActiveIndexResult={updateActiveIndexResult}
          handleSearchResultClick={handleSearchResultClick}
          mobileSearchBar={mobileSearchBar}
          handleSearchBoxFocus={handleSearchBoxFocus}
        />
      </div>
    </>
  );
};

export default HomeSearchBar;
